import React from 'react'
import { TaskService } from '@duck/api-definitions'
import { Checkbox, MultiPick, Slider } from '@duck/ui'
import { TranslationKey, useTar } from '@/modules'
import { TASK_PROFICIENCIES } from '@/utils/taskProficiencies'
import { TaskFormField } from './TaskFormField'
import { CoverImagePreview } from './CoverImagePreview'

const SLUG_REGEX = /^[a-z]+(-[a-z]+)*$/

const DIFFICULTY_OPTIONS: readonly { value: string; label: TranslationKey }[] = [
  { value: 'Easy', label: 'tasks.difficulty.easy' },
  { value: 'Intermediate', label: 'tasks.difficulty.intermediate' },
  { value: 'Hard', label: 'tasks.difficulty.hard' },
  { value: 'Expert', label: 'tasks.difficulty.expert' },
] as const

interface DetailsConfigSectionProps {
  formData: Partial<TaskService.Task>
  onChange: (
    field: keyof TaskService.Task,
    value: string | number | string[] | TaskService.TaskWeaponsOfChoiceEnum[] | boolean | undefined,
  ) => void
  fieldErrors?: Record<string, string>
  allAccessGroups: string[]
  isCreateMode: boolean
}

export const DetailsConfigSection: React.FC<DetailsConfigSectionProps> = ({
  formData,
  onChange,
  fieldErrors = {},
  allAccessGroups,
  isCreateMode,
}) => {
  const { t } = useTar()

  const weaponOptions = React.useMemo(() => {
    return [
      { value: TaskService.TaskWeaponsOfChoiceEnum.Sql, label: t('common.sql') },
      { value: TaskService.TaskWeaponsOfChoiceEnum.Python, label: t('common.python') },
    ]
  }, [t])

  const deriveSlug = React.useCallback(
    (name: string) =>
      name
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase(),
    [],
  )

  const slugPlaceholder = React.useMemo(
    () => deriveSlug(formData.name ?? ''),
    [formData.name, deriveSlug],
  )

  // Auto-fill the slug from the name while it stays auto-managed (empty, or
  // still equal to what the current name derives to). Once the user manually
  // edits the slug it diverges and we stop overwriting it.
  const handleNameChange = React.useCallback(
    (
      field: keyof TaskService.Task,
      value: string | number | string[] | TaskService.TaskWeaponsOfChoiceEnum[] | boolean | undefined,
    ) => {
      if (typeof value === 'string') {
        const currentSlug = formData.slug ?? ''
        const slugIsAutoManaged =
          currentSlug === '' || currentSlug === deriveSlug(formData.name ?? '')
        if (slugIsAutoManaged) {
          const nextSlug = deriveSlug(value)
          onChange('slug', nextSlug === '' ? undefined : nextSlug)
        }
      }
      onChange(field, value)
    },
    [formData.name, formData.slug, onChange, deriveSlug],
  )

  const slugRequiredError =
    formData.slug === undefined || formData.slug === null || formData.slug === ''
      ? t('errors.fieldRequired', { field: t('tasks.slug') })
      : null

  const slugValidationError =
    formData.slug != null && formData.slug !== '' && !SLUG_REGEX.test(formData.slug)
      ? t('errors.invalidSlugFormat')
      : null

  const isSandbox = formData.isSandboxTask === true
  const proficiencies = React.useMemo(() => {
    return Object.values(TaskService.TaskProficienciesEnum).map((proficiency) => {
      const proficiencyConfig = TASK_PROFICIENCIES[proficiency]
      return {
        label: t(proficiencyConfig.label),
        value: proficiency,
        icon: proficiencyConfig?.icon(),
      }
    })
  }, [t])

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.basicInformation')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <TaskFormField
              label="common.name"
              field="name"
              type="text"
              required
              formData={formData}
              onChange={handleNameChange}
              fieldErrors={fieldErrors}
            />
          </div>
          <div>
            <TaskFormField
              label="tasks.slug"
              field="slug"
              type="text"
              required
              tooltip={t('tasks.slugTooltip')}
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
              placeholderOverride={slugPlaceholder || undefined}
              errorOverride={
                fieldErrors.slug || slugRequiredError || slugValidationError || undefined
              }
            />
          </div>
          <div className="lg:col-span-2">
            <TaskFormField
              label="common.teaser"
              field="teaser"
              type="text"
              required
              tooltip="Displayed in Available Challenge card"
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
            />
          </div>
          <div>
            <TaskFormField
              label="tasks.coverImageUrl"
              field="coverImageUrl"
              type="text"
              tooltip="Optional base path for the cover (e.g. covers/my-task.webp). Two sizes — 1200 & 160 — are derived from it; see the preview below. Leave blank for a generated cover."
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
            />
          </div>
          {/* Vertical-focus slider sits beside the cover path, shown only once a
              cover is set (it has no effect on the generated fallback). */}
          {formData.coverImageUrl != null && formData.coverImageUrl.trim() !== '' && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">
                  {t('tasks.coverImage.focusYLabel')}
                </label>
                <span className="font-mono text-xs text-muted-foreground">
                  {formData.coverFocusY ?? 50}%
                </span>
              </div>
              {/* Slider presets a vertical position value — keep LTR so 0% is
                  always at the start regardless of UI direction. */}
              <Slider
                dir="ltr"
                min={0}
                max={100}
                step={1}
                value={[formData.coverFocusY ?? 50]}
                onValueChange={([v]) => onChange('coverFocusY', v)}
              />
              <p className="text-[11px] text-muted-foreground">
                {t('tasks.coverImage.focusYHint')}
              </p>
            </div>
          )}
          <div className="lg:col-span-2">
            <CoverImagePreview base={formData.coverImageUrl} focusY={formData.coverFocusY} />
          </div>
          <div className="lg:col-span-2">
            <TaskFormField
              label="common.description"
              field="description"
              type="markdown"
              required
              tooltip="Displayed inside the More Info dialog on a challenge"
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
            />
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.configuration')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {!isSandbox && (
            <TaskFormField
              label="common.difficulty"
              field="difficulty"
              type="select"
              required
              options={DIFFICULTY_OPTIONS}
              formData={formData}
              onChange={onChange}
              fieldErrors={fieldErrors}
            />
          )}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t('common.weapons')}
              <span className="text-error ms-1">*</span>
            </label>
            <MultiPick
              options={weaponOptions}
              selected={formData.allowedWeapons ?? []}
              onChange={(vals) => onChange('allowedWeapons', vals as TaskService.TaskWeaponsOfChoiceEnum[])}
              labels={{ placeholder: t('common.pickWeapons') }}
            />
            {fieldErrors.allowedWeapons && (
              <p className="text-sm text-error mt-1">{fieldErrors.allowedWeapons}</p>
            )}
          </div>
          {!isSandbox && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {t('common.proficiencies')}
              </label>
              <MultiPick
                options={proficiencies}
                selected={formData.proficiencies || []}
                onChange={(vals) => onChange('proficiencies', vals)}
                labels={{ placeholder: t('common.pickProficiencies') }}
              />
              {fieldErrors.proficiencies && (
                <p className="text-sm text-error mt-1">
                  {fieldErrors.proficiencies}
                </p>
              )}
            </div>
          )}
          <div className="space-y-2" data-testid="task-access-groups">
            <label className="block text-sm font-medium text-foreground">
              {t('common.accessGroups')}
              <span className="text-error ms-1">*</span>
            </label>
            <MultiPick
              options={allAccessGroups.map((g) => ({ label: g, value: g }))}
              selected={
                Array.isArray(formData.accessGroups)
                  ? (formData.accessGroups as string[])
                  : []
              }
              onChange={(vals) => onChange('accessGroups', vals)}
              labels={{ placeholder: t('common.pickAccessGroups') }}
              allowNewItem
            />
            {fieldErrors.accessGroups && (
              <p className="text-sm text-error mt-1">
                {fieldErrors.accessGroups}
              </p>
            )}
          </div>
          {!isSandbox && (
            <>
              <TaskFormField
                label="common.seedFactor"
                field="seedFactor"
                type="number"
                formData={formData}
                onChange={onChange}
                fieldErrors={fieldErrors}
              />
              <TaskFormField
                label="common.availabilityWeight"
                field="availabilityWeight"
                type="number"
                tooltip="Applies to rolling a random challenge"
                formData={formData}
                onChange={onChange}
                fieldErrors={fieldErrors}
              />
            </>
          )}
          <TaskFormField
            label="tasks.sharedDBName"
            field="sharedDBName"
            type="text"
            formData={formData}
            onChange={onChange}
            fieldErrors={fieldErrors}
          />
          <TaskFormField
            label="tasks.customQueryTimeout"
            field="customQueryTimeout"
            type="number"
            tooltip="Override the default 12 seconds enforced timeout on user queries (up to 256s)"
            formData={formData}
            onChange={onChange}
            fieldErrors={fieldErrors}
          />
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={formData.isSandboxTask === true}
                disabled={!isCreateMode}
                onCheckedChange={(v) => {
                  const checked = v === true
                  onChange('isSandboxTask', checked)
                  // Difficulty is hidden for sandboxes but still required by the contract.
                  if (checked && formData.difficulty === undefined) onChange('difficulty', TaskService.TaskDifficultyEnum.Easy)
                }}
              />
              <span title={t('tasks.isSandboxTaskTooltip')}>{t('tasks.isSandboxTask')}</span>
            </label>
            {!isCreateMode && <p className="text-xs text-muted-foreground">{t('tasks.isSandboxTaskLockedHint')}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}
